/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import Dashboard from "../../../services/api/dashboard";

function ExamReportDataIntroductory() {
  const [reportData, setReportData] = useState([]);
  const [entranceCount, setEntranceCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Fetch Yesterday + Today count
  const fetchEntranceCountData = async () => {
    try {
      const response = await Dashboard.getIntroductoryCount();
      if (response?.data && Array.isArray(response.data)) {
        setEntranceCount(response.data);
      } else {
        setError("No entrance count data available");
      }
    } catch (err) {
      console.error("Error fetching entrance count data:", err);
    }
  };

  // ✅ Fetch Area + Division Data
  const fetchReportData = async () => {
    try {
      const response = await Dashboard.getExamReportDataIntroductory();
      if (response?.data && Array.isArray(response.data)) {
        const sortedData = response.data.sort((a, b) =>
          a.areaName.localeCompare(b.areaName),
        );
        setReportData(sortedData);
      }
    } catch (err) {
      console.error("Error fetching Entrance report data:", err);
    }
  };

  const downloadAreaSummaryCsv = async (areaId) => {
    try {
      console.log("Downloading summary for areaId:", areaId);

      if (!areaId) {
        console.error("Area ID is missing");
        alert("Area ID is missing.");
        return;
      }

      const examDetailId = 55;

      const response = await Dashboard.getExamReportSummary(
        areaId,
        examDetailId,
      );

      console.log("Summary API response:", response);

      const data = response?.data?.data || [];

      if (!Array.isArray(data) || data.length === 0) {
        alert("No data available for this area.");
        return;
      }

      const headers = ["SNo.", "Area", "Cluster", "Zone", "Count"];

      const escapeCsvValue = (value) => {
        if (value === null || value === undefined) {
          return "";
        }

        const stringValue = String(value);

        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      };

      // Sort by Area → Cluster → Zone
      const sortedData = [...data].sort((a, b) => {
        const areaCompare = (a.area || "").localeCompare(
          b.area || "",
          undefined,
          { sensitivity: "base" },
        );

        if (areaCompare !== 0) {
          return areaCompare;
        }

        const clusterCompare = (a.cluster || "").localeCompare(
          b.cluster || "",
          undefined,
          { sensitivity: "base" },
        );

        if (clusterCompare !== 0) {
          return clusterCompare;
        }

        return (a.zone || "").localeCompare(b.zone || "", undefined, {
          sensitivity: "base",
        });
      });

      const csvRows = sortedData.map((item, index) => [
        index + 1,
        item.area ?? "",
        item.cluster ?? "",
        item.zone ?? "",
        item.count ?? 0,
      ]);

      const csvContent = [
        headers.map(escapeCsvValue).join(","),
        ...csvRows.map((row) => row.map(escapeCsvValue).join(",")),
      ].join("\n");

      // UTF-8 BOM for proper Excel display
      const blob = new Blob([`\uFEFF${  csvContent}`], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // Today's date
      const today = new Date();

      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();

      link.download = `Introductory Exam 2026 Summary ${day}.${month}.${year}.csv`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading area summary:", err);
      console.error("Response:", err?.response);
      console.error("Response data:", err?.response?.data);

      alert("Unable to download area summary.");
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchEntranceCountData(), fetchReportData()]);
      setLoading(false);
    };
    fetchAll();
  }, []);

  if (loading)
    return <div className="text-center mt-5 text-lg">Loading...</div>;
  if (error)
    return <div className="text-center text-red-600 mt-5 text-lg">{error}</div>;

  // ✅ Summary calculation
  let todayTotal = 0;
  let yesterdayTotal = 0;
  let summaryRows = [];

  // ✅ Summary calculation (Aug 18 First, Aug 17 Second - Descending Order)
  if (entranceCount.length > 0) {
    const sorted = [...entranceCount].sort(
      (a, b) => new Date(b.appliedDate) - new Date(a.appliedDate),
    );

    if (sorted.length >= 2) {
      todayTotal = sorted[0].totalApplications + sorted[1].totalApplications;
      yesterdayTotal = sorted[1].totalApplications;
    } else if (sorted.length === 1) {
      todayTotal = sorted[0].totalApplications;
    }

    summaryRows = sorted.map((dto, index) => {
      const isToday = index === 0;
      return {
        date: new Date(dto.appliedDate).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        }),
        total: isToday ? todayTotal : yesterdayTotal,
      };
    });
  }

  // ✅ Division Totals calculation
  const calculateTotals = () =>
    reportData.reduce(
      (acc, item) => ({
        md: acc.md + (item.md || 0),
        wd: acc.wd + (item.wd || 0),
        ymd: acc.ymd + (item.ymd || 0),
        ywd: acc.ywd + (item.ywd || 0),
        fd: acc.fd + (item.fd || 0),
      }),
      { md: 0, wd: 0, ymd: 0, ywd: 0, fd: 0 },
    );

  const totals = calculateTotals();
  const grandTotal =
    totals.md + totals.wd + totals.ymd + totals.ywd + totals.fd;

  const calculateRowTotal = (item) =>
    (item.md || 0) +
    (item.wd || 0) +
    (item.ymd || 0) +
    (item.ywd || 0) +
    (item.fd || 0);

  const thTdStyle = {
    border: "1px solid #000",
    padding: "8px 10px",
    textAlign: "center",
  };

  return (
    <div className="flex flex-col items-center mt-6 space-y-8">
      {/* ---------- Entrance Summary Table ---------- */}
      <div
        className="bg-white shadow-md rounded-lg overflow-hidden w-11/12 lg:w-8/12"
        style={{ border: "1px solid #000" }}
      >
        <h3
          className="text-center text-lg font-semibold py-3 bg-gray-100"
          style={{ marginLeft: "1%" }}
        >
          Introductory Exams Enrollment Summary
        </h3>

        <table
          className="w-full text-sm text-gray-900"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead style={{ backgroundColor: "#e5e7eb", fontWeight: "600" }}>
            <tr>
              <th aria-label="Label column" style={thTdStyle} />
              {summaryRows.map((row) => (
                <th key={row.date} style={thTdStyle}>
                  {row.date}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td
                style={{
                  ...thTdStyle,
                  textAlign: "left",
                  fontWeight: "bold",
                }}
              >
                Introductory Exam Registrations
              </td>

              {summaryRows.map((row) => {
                const isToday = row.date === summaryRows[0].date;
                return (
                  <td
                    key={row.date}
                    style={{
                      ...thTdStyle,
                      backgroundColor: isToday ? "#f3f4f6" : "#ffffff",
                      fontWeight: isToday ? "bold" : "normal",
                    }}
                  >
                    {row.total.toLocaleString()}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ---------- Area & Division Table ---------- */}
      <div
        className="bg-white shadow-md rounded-lg overflow-hidden w-11/12 lg:w-8/12"
        style={{ border: "1px solid #000" }}
      >
        <h3
          className="text-center text-lg font-semibold py-3 bg-gray-100"
          style={{ marginLeft: "1%" }}
        >
          Introductory Exam Enrollments - Area and Division wise (in descending
          order of number of enrollments)
        </h3>

        <table
          className="w-full text-sm text-gray-900"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead style={{ backgroundColor: "#e5e7eb", fontWeight: "600" }}>
            <tr>
              <th style={thTdStyle}>S.No.</th>
              <th style={thTdStyle}>Area</th>
              <th style={thTdStyle}>MD</th>
              <th style={thTdStyle}>WD</th>
              <th style={thTdStyle}>YMD</th>
              <th style={thTdStyle}>YWD</th>
              <th style={thTdStyle}>FD</th>
              <th style={thTdStyle}>Total</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map((item, index) => (
              <tr
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                }}
              >
                <td style={thTdStyle}>{index + 1}</td>
                <td style={thTdStyle}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>{item.areaName}</span>

                    <button
                      type="button"
                      onClick={() => downloadAreaSummaryCsv(item.areaId)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        color: "#2563eb",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Download Zone Summary
                    </button>
                  </div>
                </td>
                <td style={thTdStyle}>{item.md?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.wd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.ymd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.ywd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.fd?.toLocaleString() ?? 0}</td>
                <td style={{ ...thTdStyle, fontWeight: "600" }}>
                  {calculateRowTotal(item).toLocaleString()}
                </td>
              </tr>
            ))}

            <tr style={{ backgroundColor: "#e5e7eb", fontWeight: "bold" }}>
              <td colSpan="2" style={thTdStyle}>
                Total
              </td>
              <td style={thTdStyle}>{totals.md.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.wd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.ymd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.ywd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.fd.toLocaleString()}</td>
              <td style={thTdStyle}>{grandTotal.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ExamReportDataIntroductory;
